import { JsonLd } from "../components/JsonLd";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useLocalePath } from "../hooks/useLocalePath";
import { SEOHead } from "../components/SEOHead";
import { SiteFooter } from "../components/SiteFooter";
import {
  ArrowRight, ChevronDown, ChevronUp, Shield, Clock,
  AlertTriangle, CheckCircle2, FileText, Mail, Package,
} from "lucide-react";

const FAQS = [
  {
    q: "My case says approved but nothing changed. Is that normal?",
    a: "Yes. Many cases sit on \"approved\" briefly before the next visible update.",
  },
  {
    q: "How long until I receive my card?",
    a: "Some receive it quickly, others wait a few weeks.",
  },
  {
    q: "Can USCIS reverse an approval?",
    a: "Rarely, but unusual issues can still trigger follow-up notices.",
  },
  {
    q: "Should I contact USCIS immediately after approval?",
    a: "Usually no. Give it reasonable processing time first.",
  },
];

const NEXT_UPDATES = [
  { label: "Card Is Being Produced", icon: Package, color: "text-emerald-600" },
  { label: "Case Was Sent To Department of State", icon: FileText, color: "text-sky-600" },
  { label: "Approval Notice Mailed", icon: Mail, color: "text-emerald-600" },
  { label: "Interview Waived and Finalized", icon: CheckCircle2, color: "text-emerald-600" },
  { label: "No visible update for several days", icon: Clock, color: "text-slate-400" },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-4 py-3.5 text-left hover:bg-slate-50 transition-colors"
        data-testid={`faq-${q.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      >
        <span className="font-medium text-sm text-slate-800">{q}</span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">
          {a}
        </div>
      )}
    </div>
  );
}

export default function CaseApprovedPage() {
  const lp = useLocalePath();

  return (
    <div className="min-h-screen bg-white" data-testid="case-approved-page">
      <SEOHead
        title="USCIS Case Approved - What Happens Next? Full Guide (2026)"
        description="Your USCIS case was approved. Learn what usually happens next, how long it may take, what notices to expect, and what steps come after approval."
      />

      {/* JSON-LD Article Schema */}
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "USCIS Case Approved - What Happens Next? Full Guide (2026)",
            description: "Your USCIS case was approved. Learn what usually happens next, how long it may take, what notices to expect, and what steps come after approval.",
            datePublished: "2026-04-14",
            dateModified: "2026-04-14",
            url: "https://immigrationguide.us/case-approved-what-next",
            publisher: {
              "@type": "Organization",
              name: "ImmigrationGuide",
              url: "https://immigrationguide.us",
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": "https://immigrationguide.us/case-approved-what-next",
            },
          }} />

      {/* JSON-LD FAQ Schema */}
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }} />

      {/* Hero */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-14 md:py-20" data-testid="ca-hero">
        <div className="max-w-3xl mx-auto px-4">
          <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-xs font-medium px-3 py-1.5 rounded-full mb-5">
            <CheckCircle2 className="h-3.5 w-3.5" /> USCIS Approval Guide
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-5" data-testid="ca-h1">
            USCIS Case Approved - What Happens Next?
          </h1>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-4">
            Seeing <strong>Case Approved</strong> on your USCIS account can feel like a huge relief. After weeks or months of waiting, many people immediately ask the same question:
          </p>
          <p className="text-base md:text-lg font-semibold text-slate-800">
            What happens next?
          </p>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed mt-4">
            The answer depends on your case type, but approval usually means you have moved into the next stage of the immigration process.
          </p>
          <div className="mt-5 flex items-center gap-2 text-xs text-slate-500">
            <Shield className="h-3.5 w-3.5 text-emerald-600" />
            <span>Last updated: April 2026 · Based on publicly available USCIS information</span>
          </div>
        </div>
      </section>

      {/* Article body */}
      <article className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 space-y-14">

          {/* What "Case Approved" usually means */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-4">What "Case Approved" usually means</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              In most cases, USCIS has finished reviewing your application and made a positive decision.
            </p>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              That may mean:
            </p>
            <ul className="space-y-2.5">
              {[
                "Your petition was approved",
                "Your adjustment case was approved",
                "Your document request was accepted",
                "Your case is moving to card production or another agency step",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-slate-600">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-slate-600 leading-relaxed mt-4">
              This is usually one of the best updates you can receive.
            </p>
          </section>

          {/* What may happen next */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-4">What may happen next</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              After approval, one of these updates often follows:
            </p>
            <div className="space-y-2.5">
              {NEXT_UPDATES.map((s) => (
                <div key={s.label} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <s.icon className={`h-4 w-4 flex-shrink-0 ${s.color}`} />
                  <span className="text-sm font-medium text-slate-700">{s.label}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-500 mt-3 italic">
              Sometimes the next update appears quickly. Sometimes it takes a little time.
            </p>
          </section>

          {/* How long after approval */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-4">How long after approval until the next step?</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              There is no single timeline.
            </p>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              Some applicants receive the next update in 1-3 days. Others wait 1-3 weeks depending on:
            </p>
            <ul className="space-y-2 mb-4">
              {[
                "Case category",
                "USCIS service center",
                "Card printing queue",
                "Mail delivery",
                "Extra internal checks",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-slate-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* If your green card was approved */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-4">If your green card was approved</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              You may next see:
            </p>
            <ul className="space-y-2.5">
              {[
                "Card Is Being Produced",
                "Card Was Mailed To Me",
                "USPS Tracking Number Sent",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-slate-600">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-slate-600 leading-relaxed mt-4">
              Make sure your mailing address is correct.
            </p>
          </section>

          {/* If your petition was approved */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-4">If your petition was approved</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              For forms like family petitions, the case may move to another stage such as:
            </p>
            <ul className="space-y-2.5">
              {[
                "National Visa Center (NVC)",
                "Consular processing",
                "Next filing step",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-slate-600">
                  <FileText className="h-4 w-4 text-sky-600 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* What should you do now? */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-4">What should you do now?</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              Use this moment wisely:
            </p>
            <div className="space-y-2.5">
              {[
                "Save screenshots of approval updates",
                "Check mail daily",
                "Verify your address",
                "Watch for tracking numbers",
                "Keep copies of all notices",
                "Stay patient during final processing",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 text-sm text-slate-600">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-semibold mt-0.5">
                    {i + 1}
                  </span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Common mistakes after approval */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-4">Common mistakes after approval</h2>
            <div className="space-y-2.5">
              {[
                "Assuming everything is finished instantly",
                "Ignoring mailed notices",
                "Forgetting address updates",
                "Panicking if no update appears for a few days",
                "Throwing away paperwork",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3 text-sm text-slate-600">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Final takeaway */}
          <section className="bg-emerald-50 rounded-xl p-6 border border-emerald-100" data-testid="ca-takeaway">
            <h2 className="text-lg font-bold text-slate-900 mb-2">Final takeaway</h2>
            <p className="text-sm text-slate-700 leading-relaxed">
              "Case Approved" is usually a major positive milestone. In many cases, the process is not fully over yet, but you are much closer to the finish line.
            </p>
            <p className="text-sm text-slate-700 leading-relaxed mt-2">
              The next step is often paperwork, production, mailing, or transfer.
            </p>
          </section>
        </div>
      </article>

      {/* FAQ */}
      <section className="py-12 bg-slate-50 border-y border-slate-100" data-testid="ca-faq">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6">FAQ</h2>
          <div className="space-y-2">
            {FAQS.map((faq) => (
              <FAQItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 md:py-18" data-testid="ca-cta">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-base md:text-lg text-slate-700 leading-relaxed mb-6">
            Track future USCIS updates, understand notices in plain English, and stay ahead for free with ImmigrationGuide.us
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to={lp("/auth?mode=register")}
              className="inline-flex items-center justify-center gap-2 bg-teal-700 hover:bg-teal-800 text-white font-semibold px-6 py-3 rounded-lg text-sm transition-colors"
              data-testid="ca-cta-track"
            >
              Track My Case Free <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={lp("/auth?mode=register")}
              className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium px-6 py-3 rounded-lg text-sm transition-colors"
              data-testid="ca-cta-explain"
            >
              Explain My Approval Status
            </Link>
            <Link
              to={lp("/auth?mode=register")}
              className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium px-6 py-3 rounded-lg text-sm transition-colors"
              data-testid="ca-cta-alerts"
            >
              Get USCIS Alerts
            </Link>
          </div>
          <p className="mt-3 text-xs text-slate-500" data-testid="ca-cta-helper">Free account. No legal jargon. Easy status tracking.</p>
        </div>
      </section>

      {/* Related Guides */}
      <section className="py-10 border-t border-slate-100" data-testid="ca-related">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Related Guides</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link
              to={lp("/actively-reviewed-uscis-meaning")}
              className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all"
              data-testid="ca-link-reviewed"
            >
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">
                What Does "Actively Reviewed" Mean?
              </h3>
              <p className="text-xs text-slate-500">USCIS status explained in plain English</p>
            </Link>
            <Link
              to={lp("/gc/green-card-timeline")}
              className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all"
              data-testid="ca-link-timeline"
            >
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">
                Green Card Processing Times
              </h3>
              <p className="text-xs text-slate-500">Realistic timelines by category for 2026</p>
            </Link>
            <Link
              to={lp("/gc/documents-checklist")}
              className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all"
              data-testid="ca-link-docs"
            >
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">
                Documents Checklist
              </h3>
              <p className="text-xs text-slate-500">Every document you need, organized by category</p>
            </Link>
            <Link
              to={lp("/pricing")}
              className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all"
              data-testid="ca-link-pricing"
            >
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">
                Plans & Pricing
              </h3>
              <p className="text-xs text-slate-500">Unlock premium tools and AI-powered case tracking</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Block */}
      <section className="py-6 bg-slate-50 border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
            <Shield className="h-3.5 w-3.5 text-emerald-600" />
            <span>Last updated: April 2026 · Based on publicly available USCIS information. Educational content only. Not legal advice.</span>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
